import {
  Box,
  Divider,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  VStack,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';
import { FaHeartbeat, FaTachometerAlt } from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import {
  MdDevices,
  MdSettings,
  MdInventory,
  MdSettingsRemote,
  MdPrecisionManufacturing,
  MdAnalytics,
} from 'react-icons/md';
import { RiMindMap } from 'react-icons/ri';
import { useNavigate, useLocation } from 'react-router-dom';

const Sidebar = ({ collapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const sideBarBg = useColorModeValue('white', 'navy.700');
  const sidebarBorderColor = useColorModeValue('gray.200', 'navy.600');
  const activeTabBg = useColorModeValue('navy.50', 'navy.600');
  const activeTabColor = useColorModeValue('gray.700', 'white');

  const sidebarWidth = collapsed ? '50px' : '240px';

  const sidebarItems = [
    {
      label: 'Devices',
      icon: FaTachometerAlt,
      path: '/dashboard',
      color: 'teal.400',
    },
    {
      label: 'Configurations',
      icon: MdDevices,
      path: '/modbus-configs',
      color: 'orange.400',
    },
    {
      label: 'Logics',
      icon: MdSettings,
      path: '/patches',
      color: 'purple.400',
    },
    {
      label: 'Device health',
      icon: FaHeartbeat,
      path: '/device-health',
      color: 'red.400',
    },
    {
      label: 'Asset Manager',
      icon: MdInventory,
      path: '/',
      color: 'green.400',
      url: 'https://assetmanager.uniqgrid.com/',
    },
    // {
    //   label: 'SCADA',
    //   icon: MdAnalytics,
    //   path: '/',
    //   color: 'blue.500',
    //   url: 'https://ems.huskneuron.com',
    // },
  ];

  const isActiveRoute = (item) => {
    let isActive = location.pathname === item.path;

    if (
      item.path === '/modbus-configs' &&
      (location.pathname.startsWith('/modbus-configs') ||
        location.pathname.startsWith('/slave-configurations') ||
        location.pathname.startsWith('/add-configuration'))
    ) {
      isActive = true;
    }

    if (
      item.path === '/patches' &&
      (location.pathname.startsWith('/patch-configurations') ||
        location.pathname.startsWith('/add-new-patch'))
    ) {
      isActive = true;
    }

    if (
      item.path === '/device-health' &&
      (location.pathname === '/device-health' ||
        location.pathname.startsWith('/health-details'))
    ) {
      isActive = true;
    }

    return isActive;
  };

  return (
    <Box
      position="fixed"
      top="66px"
      left={0}
      height="calc(100vh - 66px - 38px)" // header + footer adjusted
      width={sidebarWidth}
      bg={sideBarBg}
      zIndex={10}
      transition="width 0.3s ease"
      boxShadow="rgba(17, 12, 46, 0.15) 0px 48px 100px 0px"
      borderRight="1px solid"
      borderColor={sidebarBorderColor}
    >
      <Divider mb={2} />

      <Flex direction="column" h="100%">
        {/* 🔼 TOP MENU */}
        <VStack
          spacing={1}
          align="stretch"
          px={collapsed ? 1 : 2}
          overflowY="auto"
        >
          {sidebarItems.map((item) => {
            const isActive = isActiveRoute(item);

            return (
              <Tooltip
                key={item.label}
                label={collapsed ? item.label : ''}
                placement="right"
                hasArrow
                isDisabled={!collapsed}
              >
                <Flex
                  align="center"
                  py={3}
                  px={2}
                  borderRadius="md"
                  bg={isActive ? activeTabBg : 'transparent'}
                  color={isActive ? activeTabColor : ''}
                  cursor="pointer"
                  gap={collapsed ? 0 : 3}
                  _hover={{ bg: activeTabBg }}
                  onClick={() => {
                    if (item.url) {
                      window.open(item.url, '_blank');
                    } else {
                      navigate(item.path);
                    }
                  }}
                >
                  <Icon as={item.icon} boxSize={5} color={item.color} />

                  {!collapsed && (
                    <>
                      <Text fontWeight="medium">{item.label}</Text>
                      {(item.label === 'Asset Manager' ||
                        item.label === 'SCADA') && (
                        <Icon as={FiExternalLink} boxSize={4} ml="auto" />
                      )}
                    </>
                  )}
                </Flex>
              </Tooltip>
            );
          })}
        </VStack>

        {/* 🔻 BOTTOM SECTION */}
        <VStack
          spacing={1}
          align="stretch"
          px={collapsed ? 1 : 2}
          mt="auto"
          mb={2}
        >
          <Tooltip
            label={collapsed ? 'Contact Us' : ''}
            placement="right"
            hasArrow
            isDisabled={!collapsed}
          >
            <Flex
              align="center"
              py={3}
              px={2}
              borderRadius="md"
              cursor="pointer"
              gap={collapsed ? 0 : 3}
              _hover={{ bg: activeTabBg }}
            >
              <Icon as={RiMindMap} boxSize={5} color="blue.400" />
              {!collapsed && <Text fontWeight="medium">Contact Us</Text>}
            </Flex>
          </Tooltip>
        </VStack>
      </Flex>
    </Box>
  );
};

export default Sidebar;
